import React, { useCallback } from 'react';
import { CheckIcon } from '@prisjakt/ui-icons';
import ChevronDown from 'mdi-react/ChevronUpIcon';
import { ListFilterItem } from 'src/components/filter-section/types';
import * as S from 'src/components/filter-section/components/list/styles';

type CheckboxProps = {
  selected: boolean;
  onClick: (id: string, value: string) => void;
  indexable?: boolean | null;
  path?: string | null;
  value: string;
  id: string;
  count?: number | null;
};

type CheckboxExpandingPanelProps = CheckboxProps & {
  isOpen: boolean;
  onClickExpandButton: () => void;
  partial: boolean;
  selectable?: boolean | null;
};

const UpIcon = <ChevronDown />;
export const CheckboxExpandingPanel = ({
  id,
  value,
  count,
  path,
  indexable,
  selectable,
  selected,
  onClick,
  isOpen,
  onClickExpandButton,
  partial,
  ...props
}: CheckboxExpandingPanelProps) => {
  const disabled = !selected && count === 0;
  const isSelectable = selectable || selectable === null;
  const onClickItem = useCallback(
    (e: React.MouseEvent<Element, MouseEvent>) => {
      e.preventDefault();
      if (!disabled && isSelectable) {
        onClick(id, value);
      }
    },
    [onClick, id, disabled, value]
  );
  return (
    <S.OptionListItem {...props}>
      <S.ExpandingCheckboxOption
        to={(indexable && path) || undefined}
        onClick={onClickItem}
        disabled={disabled}
      >
        {isSelectable && (
          <S.Checkbox checked={selected || partial}>
            <CheckIcon
              styling="neutral-light"
              strokeWidth="6"
              height={14}
              left={1}
              top={1}
              checked={selected}
              partial={partial}
            />
          </S.Checkbox>
        )}
        <S.Name>{value}</S.Name>
        {count !== undefined && <S.Count>{count}</S.Count>}
      </S.ExpandingCheckboxOption>
      <S.IconButton
        variant="icon"
        icon={UpIcon}
        isOpen={isOpen}
        onClick={onClickExpandButton}
      />
    </S.OptionListItem>
  );
};

export const Checkbox = ({
  id,
  value,
  count,
  path,
  indexable,
  selected,
  onClick,
  ...props
}: CheckboxProps) => {
  const disabled = !selected && count === 0;
  const onClickItem = useCallback(
    (e: React.MouseEvent<Element, MouseEvent>) => {
      e.preventDefault();
      if (!disabled) {
        onClick(id, value);
      }
    },
    [onClick, id, disabled, value]
  );

  return (
    <S.OptionListItem disabled={disabled}>
      <S.Option
        to={(indexable && path) || undefined}
        onClick={onClickItem}
        {...props}
      >
        <S.Checkbox checked={selected}>
          <CheckIcon
            styling="neutral-light"
            strokeWidth="6"
            height={14}
            left={1}
            top={1}
            checked={selected}
          />
        </S.Checkbox>
        <S.Name>{value}</S.Name>
        {count !== undefined && <S.Count>{count}</S.Count>}
      </S.Option>
    </S.OptionListItem>
  );
};

type Props = {
  items: ListFilterItem[];
  getSelectedState: (id: string) => boolean;
  onClickItem: (id: string, value: string) => void;
};

const Checkboxes = ({ items, getSelectedState, onClickItem }: Props) => {
  return (
    <>
      {items.map(item => (
        <Checkbox
          key={item.id}
          id={item.id}
          selected={getSelectedState(item.id)}
          onClick={onClickItem}
          value={item.value}
          count={item.count}
          path={item.path}
          indexable={item.indexable}
        />
      ))}
    </>
  );
};

export default Checkboxes;
